import { KRONOS_PREFS_SCHEMA_VERSION, userInstancePrefsStorageKey } from './constants';
import { readJsonLocal, writeJsonLocal } from './jsonLocal';

/**
 * Local JSON aligned with the future API **user↔instance junction `config`** field
 * (one object per `(userId, instanceId)`).
 */
export function emptyUserInstancePrefs() {
    return {
        __meta: { schemaVersion: KRONOS_PREFS_SCHEMA_VERSION },
        programmations: {},
        exploitationDateUi: null,
        /** `{ places_ids }` — hall multi-select on screenings list filters. */
        screeningsFiltersUi: null,
        /**
         * Plan calendar UI — `{ view, date, applicationTypeId, phaseId, sectorId, placeId }`.
         * Date/phase past checks apply on restore only.
         */
        planUi: null,
        /** `{ screenings, tasks, programmation }` — FiltersCard expanded per exploitation area. */
        filtersExpandedUi: null,
        stats: {},
        /** Tickets UI — ex. `{ batchPdfPresets: [...], batchPdfLastSalepointId: string }` */
        tickets: {},
        /** Exploitation / séance — ex. `{ bookingsExtendedView: '1' | '0' }` */
        screening: {},
        /** Appareils — ex. `{ showLongDisconnected: '1' | '0' }` */
        devices: {},
        customers: {},
        /** Per-nav-section open map: `{ [sectionName]: Record<string, boolean> }` */
        sidebar: {},
        bucketSimulator: {},
        /** `{ selectedEditionId: string|null }` — edition context for this user + instance */
        editionNavigation: null,
        /** `{ signature: string, until: number } | null` — health-check resume snooze until local midnight */
        healthCheckResumeSnooze: null,
    };
}

/**
 * @returns {object|null} Whole stored JSON — same shape as future API user↔instance junction `config`.
 */
export function readUserInstancePrefsDoc(userId, instanceId) {
    if (!userId) {
        return null;
    }
    const key = userInstancePrefsStorageKey(userId, instanceId);
    const v = readJsonLocal(key);
    if (!v || typeof v !== 'object') {
        return null;
    }
    return v;
}

export function writeUserInstancePrefsDoc(userId, instanceId, doc) {
    if (!userId) {
        return;
    }
    const key = userInstancePrefsStorageKey(userId, instanceId);
    const next = {
        ...emptyUserInstancePrefs(),
        ...doc,
        __meta: {
            ...emptyUserInstancePrefs().__meta,
            ...(doc.__meta || {}),
            schemaVersion: KRONOS_PREFS_SCHEMA_VERSION,
        },
    };
    writeJsonLocal(key, next);
}

/**
 * @param {string} userId
 * @param {string} instanceId
 * @param {string} dotPath e.g. `stats.moviesDatePreset`
 * @returns {string|null}
 */
export function readUserInstancePrefString(userId, instanceId, dotPath) {
    if (!userId) {
        return null;
    }
    const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    const parts = String(dotPath).split('.').filter(Boolean);
    let o = doc;
    for (let i = 0; i < parts.length - 1; i++) {
        o = o[parts[i]];
        if (o == null || typeof o !== 'object') {
            return null;
        }
    }
    const v = o[parts[parts.length - 1]];
    if (v == null) {
        return null;
    }
    return typeof v === 'string' ? v : String(v);
}

/**
 * @param {string} userId
 * @param {string} instanceId
 * @param {string} dotPath
 * @param {string} value
 */
export function writeUserInstancePrefString(userId, instanceId, dotPath, value) {
    if (!userId) {
        return;
    }
    const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    const parts = String(dotPath).split('.').filter(Boolean);
    let o = doc;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!o[p] || typeof o[p] !== 'object') {
            o[p] = {};
        }
        o = o[p];
    }
    o[parts[parts.length - 1]] = value;
    writeUserInstancePrefsDoc(userId, instanceId, doc);
}

/**
 * @param {string} userId
 * @param {string} instanceId
 * @param {string} section
 * @returns {Record<string, boolean>}
 */
export function readSidebarSectionOpenMap(userId, instanceId, section) {
    if (!userId) {
        return {};
    }
    const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    return doc.sidebar?.[section] || {};
}

/**
 * @param {string} userId
 * @param {string} instanceId
 * @param {string} section
 * @param {Record<string, boolean>} mapObj
 */
export function writeSidebarSectionOpenMap(userId, instanceId, section, mapObj) {
    if (!userId) {
        return;
    }
    const doc = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    doc.sidebar = doc.sidebar || {};
    doc.sidebar[section] = mapObj;
    writeUserInstancePrefsDoc(userId, instanceId, doc);
}

function mergeFlatSidebar(base, patch) {
    const out = { ...(base || {}) };
    const p = patch || {};
    Object.keys(p).forEach((sect) => {
        out[sect] = { ...(base?.[sect] || {}), ...(p[sect] || {}) };
    });
    return out;
}

/**
 * @param {string} userId
 * @param {string} instanceId
 * @param {object} patch
 */
export function mergeUserInstancePrefsPatch(userId, instanceId, patch) {
    if (!userId) {
        return null;
    }
    const cur = readUserInstancePrefsDoc(userId, instanceId) || emptyUserInstancePrefs();
    const next = {
        ...cur,
        ...patch,
        programmations: { ...cur.programmations, ...(patch.programmations || {}) },
        stats: { ...cur.stats, ...(patch.stats || {}) },
        screening: { ...(cur.screening || {}), ...(patch.screening || {}) },
        customers: { ...cur.customers, ...(patch.customers || {}) },
        bucketSimulator: { ...cur.bucketSimulator, ...(patch.bucketSimulator || {}) },
        __meta: { ...cur.__meta, ...(patch.__meta || {}), schemaVersion: KRONOS_PREFS_SCHEMA_VERSION },
    };
    if (Object.prototype.hasOwnProperty.call(patch, 'exploitationDateUi')) {
        next.exploitationDateUi = patch.exploitationDateUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'screeningsFiltersUi')) {
        next.screeningsFiltersUi = patch.screeningsFiltersUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'planUi')) {
        next.planUi = patch.planUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'filtersExpandedUi')) {
        next.filtersExpandedUi = patch.filtersExpandedUi;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'editionNavigation')) {
        next.editionNavigation = patch.editionNavigation;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'healthCheckResumeSnooze')) {
        next.healthCheckResumeSnooze = patch.healthCheckResumeSnooze;
    }
    if (patch.sidebar) {
        next.sidebar = mergeFlatSidebar(cur.sidebar, patch.sidebar);
    }
    writeUserInstancePrefsDoc(userId, instanceId, next);
    return next;
}
